import { sourceKey } from "./consolidationCoopOverview.js";
import { consolidationMillKey } from "./consolidationMillCalculation.js";
import { billedPartyLabel } from "./consolidationBillingParty.js";
import { prestationLabel } from "./consolidationSummaryPresentation.js";
import { numberLabel } from "./consolidationPresentation.js";

const number = value => Number(value) || 0;
const DIRECT = new Set(["loading", "delivery"]);
const ORDER = { inspection: 0, return: 2 };

// Explains one relevé of a coop: its steps, the day share of the shared steps and the day strip.
export function coopSourceCalculation(source, allRows, days = []) {
  const coop = consolidationMillKey(source.rows[0]?.mill);
  const dayRows = allRows.filter(row => sourceKey(row) === source.key);
  const orders = new Map(days.flatMap(day => [...day.driver_groups.flatMap(group => group.routes), ...(day.unassigned_routes || [])])
    .flatMap(route => route.orders).map(order => [order.id, order]));
  const steps = source.rows.map(row => {
    const charge = row.charge || {};
    const coefficient = charge.coefficient || {};
    const order = orders.get(charge.order_id);
    const tons = order?.delivered_tons ?? order?.planned_tons;
    return { id: row.id, kind: charge.kind, label: prestationLabel(row), block: charge.block || null,
      place: charge.kind === "fixed_fee" ? charge.order_label || "" : ["delivery", "dompeur"].includes(charge.kind) ? [charge.order_label, order?.client_name, tons != null ? `${numberLabel(tons, 3)} t` : null].filter(Boolean).join(" · ") : charge.mill?.name || "",
      sourceSeconds: number(charge.source_seconds), unit: row.unit, quantity: number(row.quantity),
      share: number(coefficient.denominator) > 0 ? number(coefficient.numerator) / number(coefficient.denominator) : 1,
      shared: !charge.block && !DIRECT.has(charge.kind) && number(coefficient.denominator) > 0,
      numerator: number(coefficient.numerator), denominator: number(coefficient.denominator),
      // A stop with several orders is split by tonnage, whatever the coop of each order.
      stopOrders: charge.kind === "delivery" && charge.block ? dayRows.filter(other => other.charge?.kind === "delivery" && other.charge.block?.visit_id === charge.block.visit_id)
        .map(other => ({ label: other.charge.order_label || "Commande", tons: number(other.charge.coefficient?.numerator), own: other.id === row.id,
          client: orders.get(other.charge.order_id)?.client_name || null, seconds: number(other.quantity) * 3600,
          otherCoop: consolidationMillKey(other.mill) === coop ? null : billedPartyLabel(other.mill) })) : [],
      cents: row.amount == null ? null : Math.round(Number(row.amount) * 100) };
  }).sort((a, b) => (ORDER[a.kind] ?? 1) - (ORDER[b.kind] ?? 1) || String(a.block?.start).localeCompare(String(b.block?.start)));
  // Direct time of each coop that day: inspection and return are shared by it.
  const direct = new Map();
  for (const row of dayRows.filter(row => DIRECT.has(row.charge?.kind) && row.unit === "heure")) {
    const key = consolidationMillKey(row.mill);
    direct.set(key, { label: billedPartyLabel(row.mill), seconds: (direct.get(key)?.seconds || 0) + number(row.quantity) * 3600 });
  }
  const visits = new Map(dayRows.filter(row => row.charge?.block && DIRECT.has(row.charge.kind)).map(row => [row.charge.block.visit_id, {
    kind: row.charge.kind, own: consolidationMillKey(row.mill) === coop, label: billedPartyLabel(row.mill),
    start: Date.parse(row.charge.block.start), end: Date.parse(row.charge.block.end) }]));
  return { steps, others: [...direct].filter(([key]) => key !== coop).map(([, value]) => value),
    coopSeconds: direct.get(coop)?.seconds || 0, daySeconds: [...direct.values()].reduce((sum, value) => sum + value.seconds, 0),
    strip: [...visits.values()].filter(visit => visit.end > visit.start).sort((a, b) => a.start - b.start) };
}
